import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Grid,
	IconButton,
	MenuItem,
	Select,
	Skeleton,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

import {
	AutoAwesome,
	Check,
	Close,
	Download,
	Edit,
	HealthAndSafety,
	Mic,
	Mood,
	MonitorHeart,
	Refresh,
	RunCircle,
	Save,
	WarningAmber,
} from '@mui/icons-material';
import GeneratedPlanTemplate from './GeneratedPlanTemplate';
import MainGrid from '../../../components/MainGrid';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
	getAccessToken,
	getElderHomeId,
	getUserId,
	getUserName,
} from '../../../Utils/AuthUtil';
import {
	getEnabledUserFeatures,
	getOptimizedCarePlan,
	getOptimizedPlanData,
} from '../../../context/MainProvider';
import { FEATURE_TYPES } from '../../../Utils/Enums';
import {
	addOptimizedCarePlan,
	fetchCarePlanById,
	fetchExternalElderPlan,
	fetchExternalLatestElderPlanData,
	fetchLatestElderPlanData,
	getResidentIds,
	updateCarePlan,
	updateExternalPlanOptimizedFlag,
} from '../../../services/ElderService';
import { toast } from 'react-toastify';
import {
	addFeedBackToPlan,
	connectWithApi,
	fetchCurrentPlan,
	generateCarePlan,
	genOptimizedPlan,
} from '../../../services/CarePlanServices';
import { sanitizeTitle } from '../../../Utils/Helpers';
import { pdf } from '@react-pdf/renderer';
import { PlanDocument } from '../../../components/PDFDocument';
import { v4 as uuidv4 } from 'uuid';
import { saveAs } from 'file-saver';
import ToastBanner from '../../../components/ToastBanner';
import { StarIcon } from '../../../assets/icons/Icons';
import { border, dark, surface, teal } from '../../../assets/theme/Colors';

// eslint-disable-next-line no-undef
const central_db_host = process.env.REACT_APP_UMS_API_URL;

const INITIAL_FEEDBACK_ERROR_STATE = {
	itemIndex: -1,
	status: false,
	msg: '',
};

const ERROR_STATE = {
	status: false,
	msg: '',
};

const alertStyles = {
	danger: {
		bg: '#fde8e8',
		border: '#f09595',
		text: '#8a2020',
	},
	warning: {
		bg: '#fef5e6',
		border: '#fac775',
		text: '#8a5c00',
	},
	info: {
		bg: '#e6f9f7',
		border: '#b3ede8',
		text: '#0a3a30',
	},
};

const tagStyles = {
	red: {
		bg: '#fde8e8',
		color: '#8a2020',
		border: '#f09595',
	},
	amber: {
		bg: '#fef3e2',
		color: '#7c5200',
		border: '#fac775',
	},
	green: {
		bg: '#e8f5ee',
		color: '#2d7a4a',
		border: '#c0dd97',
	},
};

const monitoringAlerts = [
	{
		type: 'danger',
		title: 'Fall risk — Night movement pattern',
		body: 'Arquella: repeated night movement over 7 days (+40%). 2 near-fall events logged by staff.',
	},
	{
		type: 'warning',
		title: 'Wellbeing drop — Activity engagement',
		body: 'Activity participation down to 30% (was 70%). Low mood noted in 4 daily observations.',
	},
	{
		type: 'info',
		title: 'Medication review in progress',
		body: 'Amlodipine dosage under review. Monitor BP twice daily.',
	},
];

const p = [
	{
		title: 'Mobility & Falls Prevention',
		items: [
			{
				title: 'Transfers & Mobility',
				content: [
					{
						text: 'Two-person assist for all transfers — bed to chair and bathroom visits.',
					},
					{
						text: 'Non-slip footwear at all times when mobilising. Walking frame at bedside.',
					},
					{
						text: 'Call bell within reach at all times. Check in every 2 hours during the day.',
					},
				],
			},
			{
				title: 'AI Risk Monitoring',
				content: [
					{
						text: 'Night-time check every 60 minutes from 10pm to 6am due to increased movement pattern.',
						ai: true,
					},
					{
						text: 'Upgrade to 2-person assist for all night-time bathroom visits.',
						ai: true,
					},
				],
			},
		],
	},

	{
		title: 'Cognitive & Dementia Support',
		items: [
			{
				title: 'Routine & Familiarity',
				content: [
					{
						text: 'Consistent daily routine — familiar staff where possible to reduce anxiety.',
					},
					{
						text: 'Life story approach — Maggie responds well to talking about her garden.',
					},
					{
						text: 'Avoid loud environments — can trigger anxiety.',
					},
				],
			},
			{
				title: 'Family Engagement',
				content: [
					{
						text: 'Daughter visits every Sunday — ensure Maggie is settled and prepared.',
					},
				],
			},
		],
	},

	{
		title: 'Nutrition & Hydration',
		items: [
			{
				title: 'Dietary Support',
				content: [
					{
						text: 'Soft diet — IDDSI Level 5. Meal assistance as required.',
					},
					{
						text: 'Monitor fluid intake — minimum 1500ml per day.',
					},
				],
			},
			{
				title: 'Monitoring',
				content: [
					{
						text: 'Blood glucose monitoring — twice daily before meals.',
					},
				],
			},
		],
	},

	{
		title: 'Wellbeing & Activities',
		items: [
			{
				title: 'Activities',
				content: [
					{
						text: 'Music sessions — offer 3x per week. Maggie enjoys 1940s songs.',
					},
					{
						text: 'Sensory garden visits when weather permits.',
					},
				],
			},
			{
				title: 'AI Wellbeing Recommendations',
				content: [
					{
						text: 'Daily 1:1 wellbeing check by named key worker — mood, appetite, engagement.',
						ai: true,
					},
					{
						text: 'Increase structured activity offers — minimum 2 per day due to engagement drop.',
						ai: true,
					},
				],
			},
		],
	},

	{
		title: 'Personal Care',
		items: [
			{
				title: 'Care Preferences',
				content: [
					{
						text: 'Female carers preferred — respect dignity and privacy at all times.',
					},
				],
			},
			{
				title: 'Daily Personal Support',
				content: [
					{
						text: 'Full assist with bathing, dressing, oral care. Encourage choice where possible.',
					},
					{
						text: 'Skin integrity check daily — document any concerns.',
					},
				],
			},
		],
	},

	{
		title: 'Medication',
		items: [
			{
				title: 'Current Medication',
				content: [
					{
						text: 'Donepezil 10mg — once daily with evening meal.',
					},
					{
						text: 'Metformin 500mg — twice daily with meals.',
					},
				],
			},
			{
				title: 'Medication Review',
				content: [
					{
						text: 'Amlodipine dosage under GP review — monitor BP twice daily until confirmed.',
						ai: true,
					},
				],
			},
		],
	},
];

const ActionButton = ({ children, onClick, tealBtn, startIcon, disabled }) => (
	<Button
		startIcon={startIcon}
		onClick={onClick}
		disabled={disabled}
		sx={{
			textTransform: 'none',
			borderRadius: '8px',
			fontWeight: tealBtn ? 600 : 500,
			fontSize: 13,
			px: tealBtn ? 2 : 1.8,
			py: 0.9,
			boxShadow: 'none',
			border: tealBtn ? 'none' : `1px solid ${border}`,
			backgroundColor: tealBtn ? teal : '#fff',
			color: tealBtn ? dark : '#1a2424',
			'&:hover': {
				backgroundColor: tealBtn ? '#00b0a0' : surface,
				borderColor: tealBtn ? '#00b0a0' : teal,
				boxShadow: 'none',
			},
		}}>
		{children}
	</Button>
);

const MiniTag = ({ label, type = 'amber' }) => (
	<Box
		sx={{
			display: 'inline-flex',
			alignItems: 'center',
			px: 1,
			py: '2px',
			borderRadius: '999px',
			fontSize: 11,
			fontWeight: 500,
			backgroundColor: tagStyles[type].bg,
			color: tagStyles[type].color,
			border: `1px solid ${tagStyles[type].border}`,
		}}>
		{label}
	</Box>
);

const MonitoringAlert = ({ type, title, body }) => (
	<Box
		sx={{
			backgroundColor: alertStyles[type].bg,
			borderLeft: `3px solid ${alertStyles[type].border}`,
			borderRadius: '8px',
			p: '12px 14px',
			mb: 1,
		}}>
		<Typography
			sx={{
				fontSize: 12,
				fontWeight: 600,
				color: alertStyles[type].text,
				mb: 0.5,
			}}>
			{title}
		</Typography>

		<Typography
			sx={{
				fontSize: 12,
				lineHeight: 1.5,
				color: '#6b8888',
			}}>
			{body}
		</Typography>
	</Box>
);

function CarePlanSection({ title, tagType, items }) {
	const aiTrueCount = items.reduce((count, section) => {
		return count + section.content.filter(item => item.ai === true).length;
	}, 0);

	return (
		<Box
			sx={{
				border: `1px solid ${border}`,
				borderRadius: '12px',
				overflow: 'hidden',
				mb: 1,
			}}>
			{/* HEADER */}
			<Box
				sx={{
					backgroundColor: '#e6f9f7',
					borderBottom: '1px solid #b3ede8',
					px: 2,
					py: 1.5,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}>
				<Typography
					sx={{
						fontSize: 13,
						fontWeight: 600,
						color: '#0a5050',
					}}>
					{title}
				</Typography>

				<MiniTag label={`${aiTrueCount} Changes`} type={tagType} />
			</Box>

			{/* BODY */}
			<Box sx={{ p: 2 }}>
				{items.map((item, itemIndex) => (
					<Box key={itemIndex} sx={{ mb: 2 }}>
						{/* SUB SECTION TITLE */}
						{item.label && (
							<Typography
								sx={{
									fontSize: 11,
									fontWeight: 700,
									color: '#0a5050',
									mb: 1,
									textTransform: 'uppercase',
									letterSpacing: '0.3px',
								}}>
								{item.label}
							</Typography>
						)}

						{/* CONTENT */}
						{item.content.map((content, i) => (
							<Box
								key={i}
								sx={{
									display: 'flex',
									alignItems: 'flex-start',
									gap: 1,
									fontSize: 13,
									lineHeight: 1.5,
									color: content.ai ? '#0a3a30' : '#6b8888',
									backgroundColor: content.ai
										? '#e6f9f7'
										: 'transparent',
									borderRadius: content.ai ? '6px' : 0,
									px: content.ai ? 1.2 : 0,
									py: content.ai ? 0.7 : 0,
									mb: 1,
								}}>
								{content.ai ? (
									<AutoAwesome
										sx={{
											fontSize: 14,
											mt: '2px',
											color: '#0a5050',
										}}
									/>
								) : (
									<Check
										sx={{
											fontSize: 14,
											mt: '2px',
											color: teal,
										}}
									/>
								)}
								{content.ai && (
									<Typography
										sx={{
											fontSize: 11,
											fontWeight: 700,
											color: '#0a3a30',
											mt: '2px',
											whiteSpace: 'nowrap',
										}}>
										{content.type === 'new'
											? 'New:'
											: 'Updated:'}
									</Typography>
								)}

								<Typography sx={{ fontSize: 13 }}>
									{content.text}
								</Typography>
							</Box>
						))}
					</Box>
				))}
			</Box>
		</Box>
	);
}

export default function CreateCarePlan() {
	const [showFullPlan, setShowFullPlan] = useState(false);
	const location = useLocation();
	const { state } = location;
	const navigate = useNavigate();
	const { residentId: residentIdFromUrl } = useParams();
	const token = getAccessToken();
	const homeId = getElderHomeId();
	const { optimizedCarePlan, storeOptimizedCarePlan } =
		getOptimizedCarePlan();
	const { optimizedPlanData, storeOptimizedPlanData } =
		getOptimizedPlanData();
	const [id, setId] = useState(null);
	const [sessionId, setSessionId] = useState('');
	const [idError, setIdError] = useState({ status: false, msg: '' });
	const [currentPlan, setCurrentPlan] = useState([]);
	const [isCurrentPlanFetched, setIsCurrentPlanFetched] = useState(false);
	const [currentPlanData, setCurrentPlanData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [txt, setTxt] = useState([]);
	const [fileData, setFileData] = useState([]);
	const [txtError, setTxtError] = useState({ status: false, msg: '' });
	const [popupBtnState, setPopupBtnState] = useState('');
	const [isSubPlanLoading, setIsSubPlanLoading] = useState({
		loading: false,
		planIndex: '',
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [values, setValues] = useState({ url: '', token: '' });
	const [residentIds, setResidentIds] = useState([]);
	const [residentFetchedIds, setResidentFetchedIds] = useState([]);
	const [expandedAccordion, setExpandedAccordion] = useState(null);
	const [planType, setPlanType] = useState('Current Plan');
	const [feedbackArr, setFeedbackArr] = useState([]);
	const [feedbackError, setFeedbackError] = useState(
		INITIAL_FEEDBACK_ERROR_STATE,
	);
	const { enabledUserFeatureList } = getEnabledUserFeatures();
	const [onShowFileUpload, setOnShowFileUpload] = useState(false);
	const [noReviewError, setNoReviewError] = useState(
		INITIAL_FEEDBACK_ERROR_STATE,
	);
	const [loadingMsg, setLoadingMsg] = useState('');
	const [isGenerationComplete, setIsGenerationComplete] = useState(false);
	const [showNoResidentBanner, setShowNoResidentBanner] = useState(false);
	const [navStateValues, setNavStateValues] = useState({});
	const [mainFeedback, setMainFeedback] = useState('');
	const [isOptimizingWholePlan, setIsOptimizingWholePlan] = useState(false);
	const [feedbackFiles, setFeedbackFiles] = useState([]);
	const [newInputs, setNewInputs] = useState([]);
	const [editingSection, setEditingSection] = useState(null);
	const [isPlanGenerated, setIsPlanGenerated] = useState(false);
	const [selectedResidentName, setSelectedResidentName] = useState('');
	const [currentPlanDate, setCurrentPlanDate] = useState('');
	const normalize = str => str?.toLowerCase().replace(/\s+/g, '').trim();
	const totalChanges = currentPlan.reduce((total, plan) => {
		return (
			total +
			(plan.bodyContent ?? []).reduce((sectionTotal, section) => {
				return (
					sectionTotal +
					(section.content ?? []).filter(item => item.ai === true)
						.length
				);
			}, 0)
		);
	}, 0);

	useEffect(() => {
		const residentId = localStorage.getItem('resident_id');
		setId(residentId);
		fetchResidentIds();
	}, []);

	useEffect(() => {
		if (!residentIdFromUrl || residentFetchedIds.length === 0) {
			return;
		}

		if (selectedResidentName) {
			return;
		}

		const matchedResident = residentFetchedIds.find(
			item =>
				String(item.id) === String(residentIdFromUrl) ||
				String(item.elderId) === String(residentIdFromUrl) ||
				item.name === residentIdFromUrl,
		);

		if (!matchedResident) {
			return;
		}

		const selectionValue = matchedResident.isFromExternalApi
			? matchedResident.name
			: `${matchedResident.elderId} (${matchedResident.name})`;

		onChangeSelection(selectionValue);
	}, [residentIdFromUrl, residentFetchedIds, selectedResidentName]);

	// useEffect(() => {
	// 	if (
	// 		!state?.isUpdatingPageState &&
	// 		optimizedCarePlan &&
	// 		optimizedCarePlan.formattedPlan.length > 0
	// 	) {
	// 		setCurrentPlan(optimizedCarePlan.formattedPlan);
	// 		setCurrentPlanData(optimizedCarePlan.fullPlan);
	// 		setId(optimizedPlanData.id);
	// 		setTxt(optimizedPlanData.file);
	// 		setSessionId(optimizedPlanData.sessionId);
	// 		setIsCurrentPlanFetched(true);
	// 	}
	// }, []);

	useEffect(() => {
		if (state?.id && state?.elderName && state?.fromDashboard) {
			onChangeSelection('', state.elderName);
			setNavStateValues(state);
			navigate(location.pathname, {
				replace: true,
				state: {
					...state,
					id: null,
					elderName: null,
					isExternalElder: false,
					exPlanId: null,
					fromDashboard: false,
				},
			});
		}
	}, []);

	async function fetchResidentIds() {
		const response = await getResidentIds(homeId, token, 'optimized');

		if (response.message === 'Success') {
			const newIds = response.content.map(item =>
				item.isFromExternalApi
					? item.name
					: `${item.elderId} (${item.name})`,
			);

			setResidentFetchedIds(response.content);
			setResidentIds(newIds);
			setShowNoResidentBanner(response.content.length > 0 ? false : true);
		} else {
			toast.error(response.message);
		}
	}

	function validateUploadInputs() {
		let isValid = true;

		if (!id || id === '') {
			setIdError({ status: true, msg: 'Select resident id' });
			isValid = false;
		}

		if (txt.length === 0) {
			setTxtError({ status: true, msg: 'Upload the pdf file' });
			isValid = false;
		}

		return isValid;
	}

	async function onFetchCurrentPlanFromDB(
		isExternalElder = false,
		elder = null,
	) {
		const elderId = isExternalElder ? elder.elderId : elder.id;

		setLoadingMsg('Optimizing Plan');
		setIsLoading(true);
		let response;
		let optimizedPlanIsEx = false;
		if (isExternalElder) {
			const planDetails = await fetchExternalLatestElderPlanData(
				homeId,
				elderId,
				token,
			);
			optimizedPlanIsEx = planDetails.isPlanExternal;
			if (planDetails.isPlanExternal) {
				response = await fetchExternalElderPlan(planDetails.id, token);
			} else {
				response = await fetchCarePlanById(
					planDetails.id,
					token,
					'optimized',
				);
			}
		} else {
			const manualPlanDetails = await fetchLatestElderPlanData(
				elderId,
				token,
			);
			response = await fetchCarePlanById(
				manualPlanDetails.id,
				token,
				manualPlanDetails.planType === 'optimized'
					? 'optimized'
					: 'initial',
			);
		}

		if (!response.error) {
			const fetchedPlan = optimizedPlanIsEx
				? response.carePlan.plan
				: response.plan;
			const planData = cleanCarePlan(fetchedPlan);
			storeOptimizedPlanData(id, sessionId, txt);
			setCurrentPlanDate(response.createdDate);
			onOptimizeCarePlan(elderId, planData, response.createdDate);
		} else {
			toast.error(response.message);
		}
	}

	function cleanCarePlan(plan) {
		if (!Array.isArray(plan)) return [];

		const sectionOrder = [
			'Current Situation',
			'Goals',
			'Action Description',
		];

		return plan
			.filter(item => item?.bodyContent?.length > 0)
			.map(item => {
				const orderedBodyContent = [
					...sectionOrder
						.map(key =>
							item.bodyContent.find(
								sub => normalize(sub.label) === normalize(key),
							),
						)
						.filter(Boolean),

					...item.bodyContent.filter(
						sub =>
							!sectionOrder.some(
								key => normalize(sub.label) === normalize(key),
							),
					),
				];

				return {
					title: item?.title,
					bodyContent: orderedBodyContent,
				};
			});
	}

	async function onFetchCurrentPlan(manualSelection = true, elder = null) {
		if (manualSelection) {
			const isInputsValid = validateUploadInputs();
			if (!isInputsValid) {
				return;
			}
		}

		setIsLoading(true);

		if (manualSelection && !elder) {
			const formData = new FormData();

			const selectedElder = residentFetchedIds.find(
				item => item.elderId === id.split(' ')[0],
			);

			formData.append('elder_id', selectedElder.id);
			fileData.forEach(file => {
				formData.append(
					'care_plan_pdf',
					new Blob([file], { type: 'text/plain' }),
					file.name,
				);
			});
			formData.append('use_api', false);
			formData.append('session_id', sessionId ? sessionId : uuidv4());
			formData.append('base_url', central_db_host);
			formData.append('auth_token', token);
			formData.append('client_id', getElderHomeId());

			const data = await fetchCurrentPlan(formData);
			if (!data.error) {
				const longTermPlan = data.current_plan.long_term_plan;
				const formattedPlan = longTermPlan.map(item => {
					return {
						title: item.title,
						bodyContent: Object.keys(item)
							.filter(
								value =>
									![
										'last_updated',
										'last_reviewed',
										'title',
									].includes(value) && item[value].length > 0,
							)
							.map(value => {
								return {
									label: sanitizeTitle(value),
									content: item[value],
									enableUserPrompt: false,
								};
							}),
					};
				});

				setCurrentPlan(formattedPlan);
				setCurrentPlanData(data);
				setIsCurrentPlanFetched(true);
				setIsLoading(false);
				onCloseModal();
				storeOptimizedPlanData(id, sessionId, txt);
				storeOptimizedCarePlan(formattedPlan, data);
			} else {
				toast.error(data.message);
				setIsLoading(false);
			}
		} else {
			setLoadingMsg('Generating Current plan');
			const updateOptimizedPlan = planData => {
				if (
					planData.message &&
					!planData.message.startsWith('Stream')
				) {
					setLoadingMsg(planData.message);
				}

				if (planData.message === 'Done') {
					const longTermPlan =
						planData.data.current_plan.long_term_plan;
					const formattedPlan = longTermPlan.map(item => {
						return {
							title: item.title,
							bodyContent: Object.keys(item)
								.filter(
									value =>
										![
											'last_updated',
											'last_reviewed',
											'title',
										].includes(value) &&
										item[value].length > 0,
								)
								.map(value => {
									return {
										label: sanitizeTitle(value),
										content: item[value],
										enableUserPrompt: false,
									};
								}),
						};
					});

					setCurrentPlan(formattedPlan);
					setCurrentPlanData(planData.data);
					setIsCurrentPlanFetched(true);
					setPlanType('Current Plan');
					setIsGenerationComplete(true);
					setLoadingMsg('Current care plan is created');
					setTimeout(() => {
						onResetStreamLoaders();
					}, 3000);
				}
			};

			const data = {
				elder_id: elder.elderId,
				use_api: true,
				session_id: sessionId ? sessionId : uuidv4(),
				base_url: central_db_host,
				auth_token: token,
				client_id: getElderHomeId(),
			};

			await fetchCurrentPlan(data, true, updateOptimizedPlan);
		}
	}

	async function storeOptimizedPlanInDB() {
		let selectedElder;
		if (state && state.fromDashboard) {
			selectedElder = {
				id: state.id,
				elderId: state.elderId,
				exPlanId: state.exPlanId,
			};
		} else {
			selectedElder = residentFetchedIds.find(
				item => item.elderId === id.split(' ')[0] || item.name === id,
			);
		}

		const restructuredPlan = currentPlan.map(section => {
			return {
				title: section.title,
				bodyContent: section.bodyContent.map(subsection => {
					return {
						label: subsection.label,
						content: subsection.content.map(item => {
							return item.text;
						}),
					};
				}),
			};
		});

		const planData = {
			elderHomeId: homeId,
			elderId: selectedElder.id,
			generatedUserId: getUserId(),
			generatedUserName: getUserName(),
			plan: restructuredPlan,
		};

		const formData = new FormData();
		formData.append(
			'plan',
			new Blob([JSON.stringify(planData)], {
				type: 'application/json',
			}),
		);

		fileData.forEach(file => {
			formData.append('file', file);
		});

		const response = await addOptimizedCarePlan(formData, token);

		if (!response.error) {
			toast.success('Care plan saved successfully');

			if (navStateValues && navStateValues.exPlanId) {
				if (navStateValues.isExternalElder) {
					updateExternalPlanOptimizedFlag(
						navStateValues.exPlanId,
						token,
					);
				} else {
					const data = { optimized: true };
					updateCarePlan(
						navStateValues.exPlanId,
						data,
						token,
						'initial',
					);
				}
			}
		} else {
			toast.error(response.message);
		}
	}

	const getAlertTypeFromTitle = title => {
		const lower = title.toLowerCase();

		if (lower.includes('fall risk') || lower.includes('fall')) {
			return 'danger';
		}

		if (
			lower.includes('wellbeing') ||
			lower.includes('activity') ||
			lower.includes('mood')
		) {
			return 'warning';
		}

		if (
			lower.includes('medication') ||
			lower.includes('review') ||
			lower.includes('bp')
		) {
			return 'info';
		}

		return 'info'; // default fallback
	};

	/**
	 *  This function is to optimize entire plan
	 */
	async function onOptimizeCarePlan(
		elder,
		planData,
		planDate,
		fromPrompt = false,
	) {
		if (fromPrompt) {
			setIsGenerationComplete(false);
			setLoadingMsg('Optimizing plan');
			setIsLoading(true);
		}

		setIsOptimizingWholePlan(true);
		setExpandedAccordion(null);

		const updateOptimizedPlan = payload => {
			const { event, data } = payload;

			if (event === 'refined_whole_plan') {
				const modifiedSections =
					data?.refined_whole_plan?.changes?.categories ?? [];

				const wholePlan =
					data?.refined_whole_plan?.whole_plan?.plan ??
					data?.refined_whole_plan?.plan ??
					[];

				const formattedPlan = wholePlan.map(section => {
					const sectionData = modifiedSections?.[section.title];

					const subPlans = sectionData?.subplans || {};

					const isSectionModified = section => {
						return (section || []).some(item =>
							(item.content || []).some(c => c.ai === true),
						);
					};

					return {
						title: section.title,
						bodyContent: (section.bodyContent || []).map(item => ({
							label: item.label,
							content: (Array.isArray(item.content)
								? item.content
								: [item.content]
							).map((contentItem, contentIndex) => ({
								text:
									typeof contentItem === 'string'
										? contentItem
										: contentItem.text,

								ai:
									subPlans?.[item.label]?.added?.includes(
										contentIndex,
									) ||
									subPlans?.[item.label]?.modified?.includes(
										contentIndex,
									),

								type: subPlans?.[item.label]?.added?.includes(
									contentIndex,
								)
									? 'new'
									: subPlans?.[
												item.label
										  ]?.modified?.includes(contentIndex)
										? 'updated'
										: undefined,
							})),
						})),
						isModified: isSectionModified(section.bodyContent),
					};
				});

				setCurrentPlan(formattedPlan);
			}

			if (event === 'review_tag') {
				const tag = {
					type: getAlertTypeFromTitle(data.tag),
					title: data.tag,
					body: data.masked_review,
				};

				setNewInputs(prevInputs => [...prevInputs, tag]);
			}

			if (event === 'done') {
				setIsGenerationComplete(true);
				setLoadingMsg('Optimized care plan is created');
				setIsLoading(false);
				setLoadingMsg('');
				setMainFeedback('');
				setCurrentPlanData(currentPlan);
			}

			if (data?.error) {
				toast.error(data.error);
			}
		};

		const selectedElder = residentFetchedIds.find(
			item => item.elderId === elder.split(' ')[0] || item.id === elder,
		);

		const payload = {
			instructions: mainFeedback,
			whole_plan: {
				plan: planData,
			},
			use_api: selectedElder.isFromExternalApi,
			session_id: sessionId || uuidv4(),
			base_url: central_db_host,
			auth_token: token,
			client_id: getElderHomeId(),
			elder_id: elder,
			care_plan_created_at: planDate,
		};

		await generateCarePlan(token, payload, updateOptimizedPlan);
	}

	async function onAddFeedBackToPlan(index) {
		if (
			(!feedbackArr[index] || feedbackArr[index].trim() === '') &&
			(!feedbackFiles[index] || feedbackFiles[index].length === 0)
		) {
			setFeedbackError({
				itemIndex: index,
				status: true,
				msg: 'Please add instruction/file.',
			});
			return;
		}

		setIsSubPlanLoading({
			loading: true,
			planIndex: index,
		});

		const subPlan = currentPlanData[index];

		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		const formData = new FormData();
		formData.append('elder_id', selectedElder.id);
		formData.append('session_id', sessionId);
		formData.append('sub_plan', JSON.stringify([subPlan]));
		formData.append('instructions', feedbackArr[index]);
		formData.append('use_api', onShowFileUpload ? false : true);
		if (feedbackFiles[index] && feedbackFiles[index].length > 0) {
			feedbackFiles[index].forEach(file => {
				formData.append(
					'file',
					new Blob([file], { type: 'text/plain' }),
					file.name,
				);
			});
		}

		const responseData = await addFeedBackToPlan(formData);
		const data = responseData.refined_plan;

		if (data.section) {
			const modifiedSections = data.changes;
			const updatedPlan = currentPlan.map((section, planIndex) => {
				if (index == planIndex) {
					return {
						title: section.title,

						bodyContent: (section.bodyContent || []).map(item => {
							const changes =
								modifiedSections?.[item.label] || {};

							const added = new Set(changes.added || []);
							const modified = new Set(changes.modified || []);
							const removed = new Set(changes.removed || []);

							const originalContent = Array.isArray(item.content)
								? item.content
								: [item.content];

							let updatedContent = originalContent
								.map((contentItem, contentIndex) => {
									if (removed.has(contentIndex)) return null;

									if (modified.has(contentIndex)) {
										const newValue =
											data.section?.[item.label]?.[
												contentIndex
											];

										return {
											text:
												typeof newValue === 'string'
													? newValue
													: newValue?.text || '',
											ai: true,
											type: 'updated',
										};
									}

									return {
										text:
											typeof contentItem === 'string'
												? contentItem
												: contentItem.text,
										ai: false,
										type: undefined,
									};
								})
								.filter(Boolean);

							added.forEach(contentIndex => {
								const newValue =
									data.section?.[item.label]?.[contentIndex];

								if (!newValue) return;

								updatedContent.splice(contentIndex, 0, {
									text:
										typeof newValue === 'string'
											? newValue
											: newValue?.text || '',
									ai: true,
									type: 'new',
								});
							});

							return {
								label: item.label,
								content: updatedContent,
							};
						}),
						isModified: true,
						enableUserPrompt: false,
					};
				}

				return section;
			});

			setCurrentPlan(updatedPlan);
			setPlanType('Optimized Plan');
			setIsSubPlanLoading({
				loading: false,
				planIndex: '',
			});
			handlePlanFeedbackChange('', index);
			storeOptimizedCarePlan(updatedPlan, currentPlanData);
			storeOptimizedPlanData(id, sessionId, txt);
			setNoReviewError(INITIAL_FEEDBACK_ERROR_STATE);
			setFeedbackError(INITIAL_FEEDBACK_ERROR_STATE);
			setFeedbackFiles([]);
		} else if (data.requires_feedback) {
			const updatedPlan = currentPlan.map((value, planIndex) => {
				if (index == planIndex) {
					return {
						...value,
						enableUserPrompt: true,
					};
				}

				return value;
			});

			setCurrentPlan(updatedPlan);
			setIsSubPlanLoading(prevState => ({
				...prevState,
				loading: false,
			}));

			setNoReviewError({
				itemIndex: index,
				status: true,
				msg: `${data.message}`,
			});
		} else {
			setIsSubPlanLoading(prevState => ({
				...prevState,
				loading: false,
			}));
			toast.error(data.error);
		}
	}

	async function onGeneratePdf() {
		const fileName = `SenzCare.AI_OptimizedCarePlan-${id}.pdf`;
		const elderId = onShowFileUpload ? id.split(' ')[0] : 'N/A';
		const name = onShowFileUpload ? id.split('(')[1].split(')')[0] : id;

		const blob = await pdf(
			<PlanDocument
				elderId={elderId}
				elderName={name}
				documentData={currentPlan}
				title={'Optimized Care Plan'}
			/>,
		).toBlob();
		saveAs(blob, fileName);
	}

	function readFeedBackFile(e, mainIndex) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx', 'xlsx'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0].size && e[0].size > 5242880) {
			setTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setFeedbackError(INITIAL_FEEDBACK_ERROR_STATE);
		setFeedbackFiles(prev => {
			const updated = [...prev];

			while (updated.length <= mainIndex) {
				updated.push([]);
			}

			updated[mainIndex] = [...updated[mainIndex], e[0]];

			return updated;
		});
	}

	function onRemoveFeedbackFiles(content, mainIndex) {
		setFeedbackFiles(prev => {
			const updated = [...prev];

			const files = updated[mainIndex] || [];

			updated[mainIndex] = files.filter(
				file => file.name !== content.name,
			);

			return updated;
		});
	}

	function handlePlanFeedbackChange(value, mainIndex) {
		let clone = [...feedbackArr];
		clone[mainIndex] = value;
		setFeedbackArr(clone);
		setFeedbackError(INITIAL_FEEDBACK_ERROR_STATE);
	}

	function onCloseModal() {
		setIsModalOpen(false);
		setPopupBtnState('');
		setIsLoading(false);
		setValues({ url: '', token: '' });
		setExpandedAccordion(null);
	}

	function onCreateNewPlan(restAll = true) {
		if (restAll) {
			setId(null);
			setSessionId('');
		}
		setCurrentPlan([]);
		setTxt([]);
		setFileData([]);
		setExpandedAccordion(null);
		setIsCurrentPlanFetched(false);
		setOnShowFileUpload(false);
		onResetStreamLoaders();
	}

	function onResetStreamLoaders() {
		setIsLoading(false);
		setLoadingMsg('');
		setIsGenerationComplete(false);
	}

	function onChangeSelection(value) {
		setId(value);
		setSessionId(uuidv4());
		setIdError({ status: false, msg: '' });
		onCreateNewPlan(false);
		onResetStreamLoaders();
		setMainFeedback('');

		let selectedElder;
		if (state && state.fromDashboard) {
			selectedElder = {
				id: state.id,
				elderId: state.elderId,
				exPlanId: state.exPlanId,
			};
		} else {
			selectedElder = residentFetchedIds.find(
				item =>
					item.elderId === value.split(' ')[0] || item.name === value,
			);
		}

		const residentRouteId = selectedElder?.id || selectedElder?.elderId;
		if (
			residentRouteId &&
			String(residentIdFromUrl) !== String(residentRouteId)
		) {
			navigate(`/carePlan/${residentRouteId}`, {
				replace: true,
				state: location.state,
			});
		}

		setSelectedResidentName(selectedElder.name);

		if (selectedElder?.isFromExternalApi || state?.fromDashboard) {
			setOnShowFileUpload(false);
			onFetchCurrentPlanFromDB(true, selectedElder);
		} else {
			setOnShowFileUpload(false);
			onFetchCurrentPlanFromDB(false, selectedElder);
		}

		setIsPlanGenerated(true);
	}

	function handleTextChange(sectionIndex, itemIndex, contentIndex, value) {
		const updated = [...currentPlan];
		updated[sectionIndex].bodyContent[itemIndex].content[
			contentIndex
		].text = value;
		setCurrentPlan(updated);
	}

	function onRenderPlanPreview() {
		const updatedPlans = currentPlan.filter(plan =>
			plan.bodyContent?.some(section =>
				section.content?.some(item => item.ai === true),
			),
		);

		const plansToShow =
			updatedPlans.length > 0
				? updatedPlans.slice(0, 2)
				: currentPlan.slice(0, 2);

		return plansToShow.map((section, sectionIndex) => {
			const totalUpdates = section.bodyContent.reduce(
				(count, item) =>
					count +
					item.content.filter(content => content.ai === true).length,
				0,
			);

			return (
				<CarePlanSection
					key={sectionIndex}
					title={section.title}
					tagType={totalUpdates >= 2 ? 'red' : 'amber'}
					items={section.bodyContent}
				/>
			);
		});
	}

	return (
		<MainGrid>
			<Box width={'100%'}>
				{/* HEADER */}
				<Box mb={3}>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="flex-start"
						flexWrap="wrap"
						gap={2}>
						<Box>
							<Typography
								sx={{
									fontSize: 22,
									fontWeight: 600,
									color: dark,
									mb: 0.5,
								}}>
								Care Plan Optimization
							</Typography>

							<Typography
								sx={{
									fontSize: 13,
									color: '#6b8888',
								}}>
								Review new observations and monitoring data,
								then update the care plan
							</Typography>
						</Box>

						<Select
							size="small"
							displayEmpty
							value={id || ''}
							onChange={e => onChangeSelection(e.target.value)}
							sx={{
								'& .MuiSelect-select': {
									fontSize: '13px',
									color: id ? '#000' : '#9aa5b1',
								},
							}}
							renderValue={selected => {
								if (!selected) {
									return 'Select Resident';
								}

								return selected;
							}}>
							<MenuItem
								value=""
								disabled
								sx={{ fontSize: '13px' }}>
								Select Resident
							</MenuItem>

							{residentIds.map((item, index) => (
								<MenuItem
									key={index}
									value={item}
									sx={{ fontSize: '13px' }}>
									{item}
								</MenuItem>
							))}
						</Select>
					</Stack>
				</Box>

				{isPlanGenerated && (
					<>
						{/* ALERT */}
						{newInputs.length.length > 0 && (
							<Alert
								severity="error"
								sx={{
									mb: 2,
									borderRadius: '8px',
								}}>
								<Typography sx={{ fontSize: 13 }}>
									<b>{newInputs.length} changes detected</b>{' '}
									requiring care plan updates.
								</Typography>
							</Alert>
						)}

						{/* GRID */}
						<Grid container spacing={2} mb={2}>
							{/* LEFT */}
							<Grid item xs={12} md={6}>
								<Card
									sx={{
										border: `1px solid ${border}`,
										borderRadius: '12px',
										height: '100%',
									}}>
									<CardContent sx={{ p: 2.5 }}>
										<Stack
											direction="row"
											spacing={1}
											alignItems="center"
											mb={2}>
											<Refresh sx={{ color: teal }} />

											<Typography
												sx={{
													fontSize: 13,
													fontWeight: 600,
													color: dark,
												}}>
												New inputs since last plan
											</Typography>
										</Stack>
										{newInputs.length > 0 ? (
											newInputs.map((item, index) => (
												<MonitoringAlert
													key={index}
													type={item.type}
													title={item.title}
													body={item.body}
												/>
											))
										) : (
											<Grid
												container
												mt={2}
												alignItems="center"
												justifyContent={'center'}>
												{isLoading ? (
													<Grid
														container
														flexDirection={
															'column'
														}>
														<Skeleton
															variant="rectangular"
															height={70}
															sx={{
																margin: 0,
																borderRadius:
																	'10px',
															}}
														/>
														<Skeleton
															variant="rectangular"
															height={70}
															sx={{
																marginTop:
																	'5px',
																borderRadius:
																	'10px',
															}}
														/>
													</Grid>
												) : (
													<Card
														sx={{
															borderRadius:
																'18px',
															border: '1px solid #d8e8e8',
															boxShadow: 'none',
															alignItems:
																'center',
															width: '100%',
														}}>
														<CardContent
															sx={{
																p: 3,
																display: 'flex',
																alignItems:
																	'center',
																justifyContent:
																	'center',
																minHeight: 50,
																color: '#6b8888',
																fontSize: 13,
																fontWeight: 500,
															}}>
															No new inputs since
															last plan
														</CardContent>
													</Card>
												)}
											</Grid>
										)}

										<Box mt={2}>
											<Typography
												sx={{
													fontSize: 12,
													fontWeight: 500,
													color: '#6b8888',
													mb: 1,
												}}>
												Add additional context
											</Typography>

											<Stack
												direction="row"
												spacing={1}
												alignItems="flex-start">
												<TextField
													fullWidth
													size="small"
													multiline
													minRows={2}
													maxRows={8}
													value={mainFeedback}
													onChange={e =>
														setMainFeedback(
															e.target.value,
														)
													}
													placeholder="e.g. Family reported Maggie was upset after GP visit..."
													sx={{
														'& .MuiInputBase-root':
															{
																alignItems:
																	'flex-start',
															},
														'& .MuiInputBase-input':
															{
																fontSize: 13,
																'&::placeholder':
																	{
																		fontSize: 13,
																	},
															},
													}}
													InputProps={{
														readOnly: isLoading,
													}}
												/>
												<Button
													disabled={isLoading}
													onClick={() => {
														const sElder =
															residentFetchedIds.find(
																item =>
																	item.elderId ===
																		id.split(
																			' ',
																		)[0] ||
																	item.name ===
																		id,
															);
														const elderId =
															sElder.isExternalElder
																? sElder.elderId
																: sElder.id;
														onOptimizeCarePlan(
															elderId,
															currentPlan,
															currentPlanDate,
															true,
														);
													}}
													sx={{
														textTransform: 'none',
														borderRadius: '8px',
														background: teal,
														color: dark,
														fontWeight: 600,
														boxShadow: 'none',
														'&:hover': {
															background:
																'#00b0a0',
															boxShadow: 'none',
														},
														fontSize: 13,
														justifyContent:
															'center',
													}}>
													Add
												</Button>
											</Stack>
										</Box>
									</CardContent>
								</Card>
							</Grid>

							{/* RIGHT */}
							<Grid item xs={12} md={6}>
								<Card
									sx={{
										border: `1px solid ${border}`,
										borderRadius: '12px',
										height: '100%',
									}}>
									<CardContent sx={{ p: 2.5 }}>
										<Stack
											direction="row"
											justifyContent="space-between"
											alignItems="center"
											mb={2}>
											<Stack
												direction="row"
												spacing={1}
												alignItems="center">
												<AutoAwesome
													sx={{ color: teal }}
												/>

												<Typography
													sx={{
														fontSize: 13,
														fontWeight: 600,
														color: dark,
													}}>
													AI Optimization Preview
												</Typography>
											</Stack>

											<MiniTag
												label={`${totalChanges} changes`}
												type="amber"
											/>
										</Stack>

										<Typography
											sx={{
												fontSize: 12,
												color: '#6b8888',
												mb: 1.5,
											}}>
											Highlighted sections show
											AI-suggested changes.
										</Typography>

										{currentPlan.length > 0 &&
										!isLoading ? (
											onRenderPlanPreview()
										) : isLoading ? (
											<Skeleton
												variant="rectangular"
												height={120}
												sx={{
													margin: 0,
													borderRadius: '10px',
												}}
											/>
										) : (
											<Card
												sx={{
													borderRadius: '18px',
													border: '1px solid #d8e8e8',
													boxShadow: 'none',
													alignItems: 'center',
												}}>
												<CardContent
													sx={{
														p: 3,
														display: 'flex',
														alignItems: 'center',
														justifyContent:
															'center',
														minHeight: 300,
														color: '#6b8888',
														fontSize: 13,
														fontWeight: 500,
													}}>
													No plan generated
												</CardContent>
											</Card>
										)}
									</CardContent>
								</Card>
							</Grid>
						</Grid>

						{/* ACTIONS */}
						<Stack
							direction="row"
							spacing={1}
							flexWrap="wrap"
							useFlexGap
							mb={2}>
							<ActionButton
								tealBtn
								startIcon={<Save />}
								disabled={isLoading}
								onClick={storeOptimizedPlanInDB}>
								Approve & Save Updated Plan
							</ActionButton>

							<ActionButton
								startIcon={<Edit />}
								disabled={isLoading}
								onClick={() => setShowFullPlan(!showFullPlan)}>
								View & Edit Full Plan
							</ActionButton>

							<ActionButton
								startIcon={<Download />}
								onClick={onGeneratePdf}
								disabled={isLoading}>
								Download PDF
							</ActionButton>
						</Stack>
						{showFullPlan && (
							<GeneratedPlanTemplate
								handleClose={() => setShowFullPlan(false)}
								plan={currentPlan}
								handleTextChange={handleTextChange}
								editingSection={editingSection}
								onPlanChange={value => setEditingSection(value)}
								residentName={selectedResidentName}
								isLoading={isLoading}
								onDownloadFile={onGeneratePdf}
								onSaveToProfile={storeOptimizedPlanInDB}
								totalUpdates={totalChanges}
								feedbacks={feedbackArr}
								feedbackError={feedbackError}
								onChangeFeedbackFile={readFeedBackFile}
								onRemoveFeedbackFiles={onRemoveFeedbackFiles}
								feedbackFiles={feedbackFiles}
								onAddFeedBackToPlan={onAddFeedBackToPlan}
								onChangeFeedback={handlePlanFeedbackChange}
								isSubPlanLoading={isSubPlanLoading}
							/>
						)}
					</>
				)}
			</Box>
			{isLoading && (
				<ToastBanner
					open={isLoading}
					message={loadingMsg}
					icon={<StarIcon color={'#FFFFFF'} />}
					isCompleted={isGenerationComplete}
				/>
			)}
		</MainGrid>
	);
}
